import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  Link as MuiLink,
} from '@mui/material';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';

const initiatives = [
  {
    name: 'MXSV · Mexico x Silicon Valley',
    role: 'Founding Core Team',
    description:
      "A two-day conference uniting Mexico's tech builders with the Silicon Valley ecosystem to unlock cross-border collaboration.",
    highlights: [
      'Curating founders, investors, policymakers, and students across Mexico and the US.',
      'Designing programming that mirrors the energy of Brazil at Silicon Valley with a distinctly Mexican lens.',
    ],
    link: 'https://www.mxsv.org/',
    meta: 'Stanford University · April 4-5, 2026',
    badge: 'Innovation',
  },
  {
    name: 'Mexico Trek',
    role: 'Lead Organizer',
    description:
      "An independent nonprofit trek that connects Harvard, Stanford, and MIT graduate communities with Mexico's cultural and economic landscape.",
    highlights: [
      'Scales the largest student and alumni-led trek at Harvard for two consecutive years.',
      'Designs immersive experiences that spark partnerships between global talent and Mexican leaders.',
    ],
    link: 'https://mxtrek.com/',
    meta: 'Flagship immersion across Mexico · 300+ fellows',
    badge: 'Community',
  },
  {
    name: 'Crimson Club México',
    role: 'Founding Core Team',
    description:
      'A year-round network empowering students and alumni to collaborate on civic, cultural, and professional initiatives for Mexico.',
    highlights: [
      'Launches programming that keeps the community connected to impact opportunities back home.',
      'Builds partnerships with civic leaders, cultural institutions, and global allies.',
    ],
    link: 'https://crimsonclub.mx/',
    meta: 'Global member-led platform',
    badge: 'Civic Leadership',
  },
  {
    name: 'CardinalHack @ Stanford',
    role: 'Founding Core Team',
    description:
      'A hackathon celebrating Latin American builders working on AI-powered ideas that matter back home.',
    highlights: [
      'Welcomes 300+ hackers from 40+ universities to co-create with mentors, investors, and industry partners.',
      'Produces a vibrant 24-hour sprint with music, community, and late-night tacos.',
    ],
    link: 'https://cardinalhack.com/',
    meta: 'LatAm x AI Hackathon · Stanford University',
    badge: 'Builder Culture',
  },
];

const Initiatives = () => {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'secondary.light', letterSpacing: 2 }}>
        Community leadership
      </Typography>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Initiatives
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 660, mb: 4 }}>
        I love building spaces where Mexican talent and global innovation collide. These initiatives
        create bridges for founders, researchers, and students to learn together and launch what is
        next.
      </Typography>

      <Grid container spacing={4}>
        {initiatives.map((item) => (
          <Grid item xs={12} md={6} key={item.name}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                border: '1px solid rgba(148, 163, 184, 0.16)',
                borderRadius: 4,
                boxShadow: '0 26px 56px rgba(15, 23, 42, 0.32)',
              }}
            >
              <CardContent sx={{ px: 3.5, py: 3 }}>
                <Stack spacing={2.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ gap: 2 }}
                  >
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        color: 'primary.light',
                        borderColor: 'rgba(96, 165, 250, 0.45)',
                        backgroundColor: 'rgba(2, 6, 23, 0.35)',
                      }}
                      variant="outlined"
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}
                    >
                      {item.meta}
                    </Typography>
                  </Stack>

                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                      {item.role}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {item.description}
                  </Typography>

                  <Box component="ul" sx={{ pl: 3, color: 'text.secondary', m: 0 }}>
                    {item.highlights.map((highlight) => (
                      <li key={highlight} style={{ marginBottom: '0.65rem' }}>
                        {highlight}
                      </li>
                    ))}
                  </Box>

                  <Button
                    component={MuiLink}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowOutwardRoundedIcon fontSize="small" />}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Explore initiative
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Initiatives;
